import React from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  Avatar,
  AvatarGroup,
} from "@mui/material";
import {
  FitnessCenter as FitnessCenterIcon,
  Restaurant as RestaurantIcon,
  SupervisorAccount as SupervisorAccountIcon,
  OpenInNew as OpenInNewIcon,
  History as HistoryIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import StandardButton from "../../../../../components/StandardButton";
import {
  unassignPrimaryPlanFromClient,
  trackTrainingPlansV2Event,
} from "../../training-plans-v2/services/trainingPlansV2Service";

const StripCard = styled(Card)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(2),
  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  marginBottom: theme.spacing(3),
}));

const Section = styled(Box)(() => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  gap: 6,
  minWidth: 0,
}));

const SectionLabel = styled(Typography)(({ theme }) => ({
  fontSize: 11,
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: theme.palette.text.secondary,
  display: "flex",
  alignItems: "center",
  gap: 4,
}));

const PlanName = styled(Typography)(({ theme, clickable }) => ({
  fontWeight: 700,
  fontSize: 15,
  color: theme.palette.text.primary,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  cursor: clickable ? "pointer" : "default",
  "&:hover": clickable
    ? { color: theme.palette.primary.main, textDecoration: "underline" }
    : {},
}));

const statusChipSx = (status, theme) => {
  const map = {
    active: { bg: theme.palette.success.light, color: theme.palette.success.dark },
    draft: { bg: theme.palette.warning.light, color: theme.palette.warning.dark },
    in_progress: { bg: theme.palette.info.light, color: theme.palette.info.dark },
    archived: { bg: theme.palette.grey[200], color: theme.palette.text.secondary },
  };
  const style = map[status] || map.draft;
  return {
    height: 18,
    fontSize: 10,
    fontWeight: 700,
    backgroundColor: style.bg,
    color: style.color,
    "& .MuiChip-label": { px: 1 },
  };
};

const ClientAssignmentStrip = ({
  client,
  protocol,
  activeProtocol,
  setState,
  getMealPlanById,
  coachesArray,
}) => {
  const navigate = useNavigate();
  const safeCoachesArray = coachesArray ?? [];
  const [removingPlan, setRemovingPlan] = React.useState(false);

  const assignedPlanId = client?.primaryProtocolId || client?.protocolId;
  const planData = protocol?.data?.();
  const planName =
    planData?.name ||
    planData?.title ||
    planData?.v2Meta?.name ||
    null;
  const planId = protocol?.id || assignedPlanId;
  const activePlanData = activeProtocol?.data?.();
  const activePlanName =
    activePlanData?.name ||
    activePlanData?.title ||
    activePlanData?.v2Meta?.name ||
    null;
  const planStatus = planData?.v2Meta?.status || (planId ? "active" : null);
  const mealPlan = client?.mealPlanId ? getMealPlanById?.(client.mealPlanId) : null;
  const mealPlanName = mealPlan?.name ?? null;

  const handleManagePlan = () => {
    if (planId) {
      navigate(`/home/training-plans-v2/${planId}/manage`);
    } else {
      navigate(`/home/training-plans-v2?clientUid=${client.uid}`);
    }
  };

  const handleOpenPlanName = () => {
    if (planId) {
      navigate(`/home/training-plans-v2/${planId}/manage`);
    }
  };

  const handleRemoveTrainingPlan = async () => {
    const primaryProtocolId = client?.protocolId || null;
    if (!primaryProtocolId || !client?.uid) {
      return;
    }
    const displayName =
      `${client?.firstName || ""} ${client?.lastName || ""}`.trim() || client.uid;
    const shouldContinue = window.confirm(
      `Remove the assigned training plan for ${displayName}? They will have no training plan until you assign a new one.`,
    );
    if (!shouldContinue) {
      return;
    }
    setRemovingPlan(true);
    try {
      await unassignPrimaryPlanFromClient({
        clientId: client.uid,
        planId: primaryProtocolId,
        sourcePlanName: planName,
        clientNameSnapshot: displayName,
      });
      await trackTrainingPlansV2Event("plan_primary_assignment_remove", {
        planId: primaryProtocolId,
        clientId: client.uid,
        entrypoint: "client_assignment_strip",
      });
    } catch (error) {
      window.alert(error?.message || "Failed to remove training plan assignment.");
    } finally {
      setRemovingPlan(false);
    }
  };

  return (
    <StripCard>
      <CardContent sx={{ py: 2, px: 3, "&:last-child": { pb: 2 } }}>
        <Stack
          direction="row"
          divider={<Divider orientation="vertical" flexItem />}
          spacing={3}
          alignItems="flex-start"
        >
          {/* ── Training Plan ── */}
          <Section>
            <SectionLabel>
              <FitnessCenterIcon sx={{ fontSize: 13 }} />
              Training Plan
            </SectionLabel>

            <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
              <PlanName
                clickable={planId ? 1 : 0}
                onClick={planId ? handleOpenPlanName : undefined}
                title={planName}
              >
                {planName ||
                  (assignedPlanId
                    ? "Unable to load assigned plan"
                    : "No plan assigned")}
              </PlanName>
              {activePlanName && (
                <Tooltip
                  title={`Client is currently training on this plan in the app: ${activePlanName}`}
                >
                  <Chip label="Active in app" size="small" color="info" variant="outlined" />
                </Tooltip>
              )}
              {planStatus && (
                <Chip
                  label={planStatus.replace("_", " ")}
                  size="small"
                  sx={(theme) => statusChipSx(planStatus, theme)}
                />
              )}
              {planId && (
                <Tooltip title="Open in Training Plans">
                  <IconButton
                    size="small"
                    sx={{ p: 0.25 }}
                    onClick={handleManagePlan}
                  >
                    <OpenInNewIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center">
              <StandardButton
                size="small"
                variant={planId ? "secondary" : "primary"}
                onClick={handleManagePlan}
              >
                {planId ? "Manage Plans" : "Assign Plan"}
              </StandardButton>
              {client?.protocolId ? (
                <StandardButton
                  size="small"
                  variant="secondary"
                  disabled={removingPlan}
                  onClick={handleRemoveTrainingPlan}
                >
                  {removingPlan ? "Removing…" : "Remove assignment"}
                </StandardButton>
              ) : null}
              {planId && (
                <Tooltip title="View plan history">
                  <IconButton
                    size="small"
                    onClick={() =>
                      setState((prev) => ({ ...prev, protocolHistoryOpen: true }))
                    }
                  >
                    <HistoryIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          </Section>

          {/* ── Meal Plan ── */}
          <Section>
            <SectionLabel>
              <RestaurantIcon sx={{ fontSize: 13 }} />
              Meal Plan
            </SectionLabel>

            <PlanName title={mealPlanName}>
              {mealPlanName || "No plan assigned"}
            </PlanName>

            <StandardButton
              size="small"
              variant={mealPlanName ? "secondary" : "primary"}
              onClick={() =>
                setState((prev) => ({ ...prev, mealPlanSelectOpen: true }))
              }
            >
              {mealPlanName ? "Change Plan" : "Assign Plan"}
            </StandardButton>
          </Section>

          {/* ── Coaches ── */}
          <Section>
            <SectionLabel>
              <SupervisorAccountIcon sx={{ fontSize: 13 }} />
              Coaches
            </SectionLabel>

            {safeCoachesArray.length > 0 ? (
              <Stack direction="row" alignItems="center" spacing={1}>
                <AvatarGroup
                  max={4}
                  sx={{
                    "& .MuiAvatar-root": { width: 28, height: 28, fontSize: 12 },
                  }}
                >
                  {safeCoachesArray.map((coach, i) => (
                    <Tooltip
                      key={coach?.key || coach?.uid || i}
                      title={`${coach?.firstName || ""} ${coach?.lastName || ""}`.trim()}
                    >
                      <Avatar src={coach?.url} sx={{ width: 28, height: 28 }}>
                        {coach?.firstName?.[0]}
                        {coach?.lastName?.[0]}
                      </Avatar>
                    </Tooltip>
                  ))}
                </AvatarGroup>
                <Typography variant="caption" color="text.secondary">
                  {safeCoachesArray.length} coach{safeCoachesArray.length !== 1 ? "es" : ""}
                </Typography>
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: 14 }}>
                No coaches assigned
              </Typography>
            )}

            <StandardButton
              size="small"
              variant={safeCoachesArray.length ? "secondary" : "primary"}
              onClick={() =>
                setState((prev) => ({ ...prev, coachSelectOpen: true }))
              }
              startIcon={!safeCoachesArray.length ? <AddIcon /> : undefined}
            >
              {safeCoachesArray.length ? "Manage Coaches" : "Assign Coach"}
            </StandardButton>
          </Section>
        </Stack>
      </CardContent>
    </StripCard>
  );
};

export default ClientAssignmentStrip;
